import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Project } from '@brg/api'
import { Skeleton } from '@brg/ui'
import SortableList, { SortableItem } from 'react-easy-sort'
import styled, { createGlobalStyle } from 'styled-components'
import { LayoutConst } from 'core'
import { SortableGridWrapperNew } from 'shared/components/grids'
import { arrayMoveImmutable } from 'shared/utils/arrayMove'
import { ItemType } from 'shared/constants/itemType'
import { TeamTabProjectOrderCard } from './TeamTabProjectOrderCard'
import { isTeamTabProjectWideCover } from './teamTabProjectCoverLayout'

const SortableHelperStyles = createGlobalStyle`
  .sortable-projects-grid-dragged {
    opacity: 1 !important;
    z-index: 300100 !important;
    cursor: grabbing;
  }
`

const OrderGridWrapper = styled(SortableGridWrapperNew)`
  .sortable-projects-grid {
    grid-auto-flow: dense;
    justify-content: center;
  }

  .sortable-projects-grid-item {
    cursor: default;
    width: 100%;
  }

  .sortable-projects-grid-item--wide {
    grid-column: span 2;
  }

  .sortable-projects-grid-dragged {
    opacity: 1 !important;
    z-index: 300100 !important;
  }
`

const SkeletonCardRoot = styled.div<{ $wide?: boolean; $wideAspect?: boolean }>`
  position: relative;
  width: ${({ $wide }) => ($wide ? '100%' : `${LayoutConst.projectCard.l.w}px`)};
  height: ${({ $wide, $wideAspect }) => (
    $wide
      ? `${LayoutConst.projectCard.l[$wideAspect ? 'h1_9' : 'h']}px`
      : `${LayoutConst.projectCard.l.h}px`
  )};
  max-width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  background-color: #f2f2f2;

  @media (max-width: ${LayoutConst.screen.l}px) {
    width: ${({ $wide }) => ($wide ? '100%' : `${LayoutConst.projectCard.m.w}px`)};
    height: ${({ $wide, $wideAspect }) => (
      $wide
        ? `${LayoutConst.projectCard.m[$wideAspect ? 'h1_9' : 'h']}px`
        : `${LayoutConst.projectCard.m.h}px`
    )};
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: ${({ $wide }) => ($wide ? '100%' : `${LayoutConst.projectCard.s.w}px`)};
    height: ${({ $wide, $wideAspect }) => (
      $wide
        ? `${LayoutConst.projectCard.s[$wideAspect ? 'h1_9' : 'h']}px`
        : `${LayoutConst.projectCard.s.h}px`
    )};
  }
`

const getGridItemClassName = (project: Project) => {
  const base = 'sortable-projects-grid-item'
  if (isTeamTabProjectWideCover(project)) {
    return `${base} sortable-projects-grid-item--wide`
  }
  return base
}

type TeamTabProjectsOrderGridSkeletonProps = {
  count?: number
}

export const TeamTabProjectsOrderGridSkeleton: FC<TeamTabProjectsOrderGridSkeletonProps> = ({
  count = 8,
}) => (
  <OrderGridWrapper mobileColumns={1} itemType={ItemType.other}>
    <div className="sortable-projects-grid team-tab-projects-order-grid">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="sortable-projects-grid-item">
          <SkeletonCardRoot>
            <Skeleton width="100%" height="100%" />
          </SkeletonCardRoot>
        </div>
      ))}
    </div>
  </OrderGridWrapper>
)

type TeamTabProjectsOrderGridProps = {
  projects: Project[]
  entityType?: string
  selectedProjectIds: Set<string>
  onToggleProject: (projectId: string) => void
  onSortEnd?: (projectId: string, newIndex: number) => void
  draggable?: boolean
}

const renderProjectCard = (
  project: Project,
  entityType: string | undefined,
  selectedProjectIds: Set<string>,
  onToggleProject: (projectId: string) => void,
  showDragHandle: boolean,
) => (
  <TeamTabProjectOrderCard
    project={project}
    entityType={entityType}
    selected={selectedProjectIds.has(project.projectId)}
    showDragHandle={showDragHandle}
    onToggleSelect={onToggleProject}
  />
)

export const TeamTabProjectsOrderGrid: FC<TeamTabProjectsOrderGridProps> = ({
  projects,
  entityType,
  selectedProjectIds,
  onToggleProject,
  onSortEnd,
  draggable = true,
}) => {
  const [projectsSource, setProjectsSource] = useState(projects)

  const projectsOrderKey = useMemo(
    () => projects.map((project) => project.projectId).join(','),
    [projects],
  )

  useEffect(() => {
    setProjectsSource(projects)
  }, [projectsOrderKey, projects])

  const onProjectsSortEnd = useCallback((oldIndex: number, newIndex: number) => {
    const project = projectsSource[oldIndex]
    if (!project) return

    setProjectsSource(arrayMoveImmutable(projectsSource, oldIndex, newIndex))
    onSortEnd?.(project.projectId, newIndex)
  }, [projectsSource, onSortEnd])

  if (!draggable) {
    return (
      <OrderGridWrapper mobileColumns={1} itemType={ItemType.other}>
        <div className="sortable-projects-grid team-tab-projects-order-grid">
          {projects.map((project) => (
            <div key={project.projectId} className={getGridItemClassName(project)}>
              {renderProjectCard(project, entityType, selectedProjectIds, onToggleProject, false)}
            </div>
          ))}
        </div>
      </OrderGridWrapper>
    )
  }

  return (
    <OrderGridWrapper mobileColumns={1} itemType={ItemType.other}>
      <SortableHelperStyles />
      <SortableList
        onSortEnd={onProjectsSortEnd}
        className="sortable-projects-grid team-tab-projects-order-grid"
        draggedItemClassName="sortable-projects-grid-dragged"
      >
        {projectsSource.map((project) => (
          <SortableItem key={project.projectId}>
            <div className={getGridItemClassName(project)}>
              {renderProjectCard(project, entityType, selectedProjectIds, onToggleProject, true)}
            </div>
          </SortableItem>
        ))}
      </SortableList>
    </OrderGridWrapper>
  )
}
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Project } from '@brg/api'
import { Skeleton } from '@brg/ui'
import SortableList, { SortableItem } from 'react-easy-sort'
import styled, { createGlobalStyle } from 'styled-components'
import { LayoutConst } from 'core'
import { SortableGridWrapperNew } from 'shared/components/grids'
import { arrayMoveImmutable } from 'shared/utils/arrayMove'
import { ItemType } from 'shared/constants/itemType'
import { TeamTabProjectOrderCard } from './TeamTabProjectOrderCard'
import { isTeamTabProjectWideCover } from './teamTabProjectCoverLayout'

const SortableHelperStyles = createGlobalStyle`
  .sortable-projects-grid-dragged {
    opacity: 1 !important;
    z-index: 300100 !important;
    cursor: grabbing;
  }
`

const OrderGridWrapper = styled(SortableGridWrapperNew)`
  .sortable-projects-grid {
    grid-auto-flow: dense;
    justify-content: center;
  }

  .sortable-projects-grid-item {
    cursor: default;
    width: 100%;
  }

  .sortable-projects-grid-item--wide {
    grid-column: span 2;
  }

  .sortable-projects-grid-dragged {
    opacity: 1 !important;
    z-index: 300100 !important;
  }
`

const SkeletonCardRoot = styled.div<{ $wide?: boolean; $wideAspect?: boolean }>`
  position: relative;
  width: ${({ $wide }) => ($wide ? '100%' : `${LayoutConst.projectCard.l.w}px`)};
  height: ${({ $wide, $wideAspect }) => (
    $wide
      ? `${LayoutConst.projectCard.l[$wideAspect ? 'h1_9' : 'h']}px`
      : `${LayoutConst.projectCard.l.h}px`
  )};
  max-width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  background-color: #f2f2f2;

  @media (max-width: ${LayoutConst.screen.l}px) {
    width: ${({ $wide }) => ($wide ? '100%' : `${LayoutConst.projectCard.m.w}px`)};
    height: ${({ $wide, $wideAspect }) => (
      $wide
        ? `${LayoutConst.projectCard.m[$wideAspect ? 'h1_9' : 'h']}px`
        : `${LayoutConst.projectCard.m.h}px`
    )};
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: ${({ $wide }) => ($wide ? '100%' : `${LayoutConst.projectCard.s.w}px`)};
    height: ${({ $wide, $wideAspect }) => (
      $wide
        ? `${LayoutConst.projectCard.s[$wideAspect ? 'h1_9' : 'h']}px`
        : `${LayoutConst.projectCard.s.h}px`
    )};
  }
`

const getGridItemClassName = (project: Project) => {
  const base = 'sortable-projects-grid-item'
  if (isTeamTabProjectWideCover(project)) {
    return `${base} sortable-projects-grid-item--wide`
  }
  return base
}

type TeamTabProjectsOrderGridSkeletonProps = {
  count?: number
}

export const TeamTabProjectsOrderGridSkeleton: FC<TeamTabProjectsOrderGridSkeletonProps> = ({
  count = 8,
}) => (
  <OrderGridWrapper mobileColumns={1} itemType={ItemType.other}>
    <div className="sortable-projects-grid team-tab-projects-order-grid">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="sortable-projects-grid-item">
          <SkeletonCardRoot>
            <Skeleton width="100%" height="100%" />
          </SkeletonCardRoot>
        </div>
      ))}
    </div>
  </OrderGridWrapper>
)

type TeamTabProjectsOrderGridProps = {
  projects: Project[]
  entityType?: string
  selectedProjectIds: Set<string>
  onToggleProject: (projectId: string) => void
  onSortEnd?: (projectId: string, newIndex: number) => void
  draggable?: boolean
}

const renderProjectCard = (
  project: Project,
  entityType: string | undefined,
  selectedProjectIds: Set<string>,
  onToggleProject: (projectId: string) => void,
  showDragHandle: boolean,
) => (
  <TeamTabProjectOrderCard
    project={project}
    entityType={entityType}
    selected={selectedProjectIds.has(project.projectId)}
    showDragHandle={showDragHandle}
    onToggleSelect={onToggleProject}
  />
)

export const TeamTabProjectsOrderGrid: FC<TeamTabProjectsOrderGridProps> = ({
  projects,
  entityType,
  selectedProjectIds,
  onToggleProject,
  onSortEnd,
  draggable = true,
}) => {
  const [projectsSource, setProjectsSource] = useState(projects)

  const projectsOrderKey = useMemo(
    () => projects.map((project) => project.projectId).join(','),
    [projects],
  )

  useEffect(() => {
    setProjectsSource(projects)
  }, [projectsOrderKey, projects])

  const onProjectsSortEnd = useCallback((oldIndex: number, newIndex: number) => {
    const project = projectsSource[oldIndex]
    if (!project) return

    setProjectsSource(arrayMoveImmutable(projectsSource, oldIndex, newIndex))
    onSortEnd?.(project.projectId, newIndex)
  }, [projectsSource, onSortEnd])

  if (!draggable) {
    return (
      <OrderGridWrapper mobileColumns={1} itemType={ItemType.other}>
        <div className="sortable-projects-grid team-tab-projects-order-grid">
          {projects.map((project) => (
            <div key={project.projectId} className={getGridItemClassName(project)}>
              {renderProjectCard(project, entityType, selectedProjectIds, onToggleProject, false)}
            </div>
          ))}
        </div>
      </OrderGridWrapper>
    )
  }

  return (
    <OrderGridWrapper mobileColumns={1} itemType={ItemType.other}>
      <SortableHelperStyles />
      <SortableList
        onSortEnd={onProjectsSortEnd}
        className="sortable-projects-grid team-tab-projects-order-grid"
        draggedItemClassName="sortable-projects-grid-dragged"
      >
        {projectsSource.map((project) => (
          <SortableItem key={project.projectId}>
            <div className={getGridItemClassName(project)}>
              {renderProjectCard(project, entityType, selectedProjectIds, onToggleProject, true)}
            </div>
          </SortableItem>
        ))}
      </SortableList>
    </OrderGridWrapper>
  )
}
